import * as React from 'react';

import {InternalSurveyModal} from 'shells/modal/internal-survey-modal/internal-survey-modal';

import type {EntitySummaryRating} from 'nutshell-graphql-types';

import {useFeedbackSubmit} from '../graphql/hooks/use-feedback-submit';

type RateFunction = (rating: EntitySummaryRating) => Promise<any>;

type LeadRecapSurveyResult = {
    isOpen: boolean;
    openSurvey: () => void;
    /** Wraps the provided onRate to open survey on negative ratings */
    handleRate: RateFunction;
    SurveyModal: React.ReactNode;
};

type UseLeadRecapSurveyOptions = {
    onRate: RateFunction;
    entitySummaryId?: string;
};

export function useLeadRecapSurvey(options: UseLeadRecapSurveyOptions): LeadRecapSurveyResult {
    const {onRate, entitySummaryId} = options;
    const [isOpen, setIsOpen] = React.useState(false);
    const {feedbackSubmit} = useFeedbackSubmit();

    const openSurvey = () => {
        setIsOpen(true);
    };

    const closeSurvey = () => {
        setIsOpen(false);
    };

    const handleRate = React.useCallback(
        (rating: EntitySummaryRating) => {
            if (rating === 'NEGATIVE') {
                openSurvey();
            }

            return onRate(rating);
        },
        [onRate]
    );

    const handleSubmit = React.useCallback(
        (response: string) => {
            const fullResponse = entitySummaryId
                ? `${response}\n\nEntity Summary ID: ${entitySummaryId}`
                : response;

            return feedbackSubmit({
                key: 'lead-recap',
                response: fullResponse,
                uri: window.location.href,
            });
        },
        [feedbackSubmit, entitySummaryId]
    );

    const SurveyModal = isOpen ? (
        <InternalSurveyModal
            onClose={closeSurvey}
            title='Tell us what you think about AI lead recaps and next steps'
            description='Did you find the summaries and next steps accurate or helpful? Is there anything we should imrpove about this feature?'
            onSubmit={handleSubmit}
            thumbnailIllustration='/include/images/onboarding/lead-recaps-feedback.svg'
        />
    ) : null;

    return {
        isOpen,
        openSurvey,
        handleRate,
        SurveyModal,
    };
}
